// @flow

import * as React from "react";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";

import RadioGroup from "../RadioGroup";
import useDatePickerState from "../../hooks/useDatePickerState";
import Accordion from "../Accordion";
import useMergableState from "../../hooks/useMergableState";
import { drupalDate } from "../../constants/Validators";
import SharedFilterFunctions from "./SharedFilterFunctions";
import CustomField from "../CustomField";

import * as GTM from "../../modules/tag-manager";

const { useState, useEffect } = React;

type PropsType = {
    data: any,
    resetUrl: string,
};

const EventListingFilters = (props: PropsType): React.Node => {
    // Get data from props
    const { dates, search, category: eventTypes, suburb: suburbs } = props.data;

    // State vars
    const [showDateFields, setShowDateFields] = useState(false);
    const { datePickersState, setDateFieldValue } = useDatePickerState({
        [`datepicker-${dates.from.name}`]: dates.from.value,
        [`datepicker-${dates.to.name}`]: dates.to.value,
    });
    const [inputState, setInputState] = useMergableState({
        [search.name]: search.value,
    });

    // Factory functions
    const {
        renderFilterActions,
        renderDateField,
        createFilterGroup,
        filterGroupLooper,
        renderSearchField,
    } = new SharedFilterFunctions(
        inputState,
        setInputState,
        "Event Listing Filters"
    );

    const filterGroups = [
        createFilterGroup("checkbox", "Event type", eventTypes),
        createFilterGroup("checkbox", "Suburb", suburbs),
    ];

    useEffect(() => {
        if (dates.options[dates.options.length - 1].active) {
            setShowDateFields(true);
        }
    }, [dates.options]);

    useEffect(() => {
        setInputState(
            dates.options.reduce((total: any, date: any): any => {
                if (date.active) {
                    return {
                        ...total,
                        [date.name]: date.value,
                    };
                }
                return total;
            }, {})
        );
    }, []);

    const renderRadio = (date: any, index: number): React.Node => (
        <ReactFormField
            key={index}
            type="radio"
            label={date.label}
            name={date.name}
            id={date.value}
            value={date.value || ""}
            // defaultChecked={date.active}
            checked={inputState[date.name] === date.value}
            onChange={() => {
                setInputState({ [date.name]: date.value });
                // The last field in this list should be the 'select dates'
                // fields - if that one is selected, show the to and from fields
                if (index === dates.options.length - 1) {
                    setShowDateFields(true);
                } else {
                    setShowDateFields(false);
                }
                GTM.customEvent({
                    event: "filter-radio-changed",
                    filter_radio_group: "Dates",
                    filter_radio_label: date.label,
                });
            }}
            fieldComponent={CustomField}
            hideOptional
        ></ReactFormField>
    );

    const renderDateFields = (): React.Node => (
        <div className="filters__date-fields">
            {renderDateField(
                {
                    name: dates.from.name,
                    label: "From",
                    required: true,
                },
                datePickersState,
                setDateFieldValue
            )}
            {renderDateField(
                {
                    name: dates.to.name,
                    label: "To",
                    required: true,
                },
                datePickersState,
                setDateFieldValue
            )}
        </div>
    );

    return (
        <div className="filters__outer">
            <ReactForm pageSubmit disableErrorScroll validators={[drupalDate]}>
                <div className="filters__inner">
                    <div className="filters__inputs">
                        {/* Search field */}
                        <div className="filters__search">
                            {renderSearchField(search.name)}
                        </div>
                        {/* Accordions */}
                        <div className="filters__accordions">
                            {/* Dates accordion */}
                            <Accordion title="Dates">
                                <div className="accordion-padded-content">
                                    <RadioGroup label="">
                                        <div className="radio-group-col">
                                            {dates.options.map(renderRadio)}
                                        </div>
                                    </RadioGroup>
                                    {showDateFields ? renderDateFields() : null}
                                </div>
                            </Accordion>
                            {/* Filter groups */}
                            {filterGroupLooper(filterGroups)}
                        </div>
                    </div>
                    {/* Actions */}
                    {renderFilterActions(props.resetUrl)}
                </div>
            </ReactForm>
        </div>
    );
};

export default EventListingFilters;
